import * as dayjs from 'dayjs';
import { TimeZone } from '../../time/timezone.js';
import { MapUtils } from '../../utility/map-utils.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { SyncEvent, SyncEventSource } from '../sync-event.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { OpenAccessPassTemplate } from './open-access-pass/open-access-pass-template.js';

export class OpenAccessPassTemplateCreatedEvent extends SyncEvent {
  static readonly TYPE_ID: string = '5be9df6355b0464abd7d64184a9ee481';

  constructor(
      public readonly uniqueId: string,
      public readonly creatorId: string | null,
      public readonly deviceId: string | null,
      public readonly creationDate: dayjs.Dayjs,
      public readonly source: SyncEventSource,
      public readonly template: OpenAccessPassTemplate,
  ) {
    super();
  }

  get $typeId(): string {
    return OpenAccessPassTemplateCreatedEvent.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): OpenAccessPassTemplateCreatedEvent {
    return new OpenAccessPassTemplateCreatedEvent(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'creatorId'),
        MapUtils.getOrNull<string>(map, 'deviceId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<string>(map, 'source'), SyncEventSource),
        context.fromData<OpenAccessPassTemplate>(MapUtils.get<SerializedData>(map, 'template')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'templateId': this.template.uniqueId,
      'template': context.toData(this.template),
    };
  }

  clone(): this {
    return new OpenAccessPassTemplateCreatedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        this.template.clone(),
    ) as this;
  }

  isSame(other: OpenAccessPassTemplateCreatedEvent): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.deviceId === this.deviceId &&
        other.creationDate === this.creationDate &&
        other.source === this.source &&
        isSameData(other.template, this.template);
  }

  static verifyType(dm: DataModel): boolean {
    return dm instanceof OpenAccessPassTemplateCreatedEvent;
  }

  static assertType(dm: DataModel): dm is OpenAccessPassTemplateCreatedEvent {
    return dm instanceof OpenAccessPassTemplateCreatedEvent;
  }
}

export class OpenAccessPassTemplateCreatedEventBuilder extends DataModelBuilder<OpenAccessPassTemplateCreatedEvent> {
  build(context: SerializationContext, map: SerializedData): OpenAccessPassTemplateCreatedEvent {
    return OpenAccessPassTemplateCreatedEvent.fromMap(context, map);
  }
}
